import { useState, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  ComposedChart,
  Line,
} from 'recharts';
import { ArrowPathIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import KPICard from '../components/KPICard';
import { useHubSpotMarketing } from '../hooks/useHubSpotMarketing';
import { useSearchConsole } from '../hooks/useSearchConsole';
import { formatNumber, formatDate } from '../utils/formatters';
import type { EmailRow } from '../types/hubspot';
import type { GSCDateRange, GSCQueryRow, GSCPageRow } from '../types/searchConsole';

function isSafeUrl(raw: string): boolean {
  try {
    const { protocol } = new URL(raw);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

const DATE_RANGES: { value: GSCDateRange; label: string }[] = [
  { value: '7d', label: '7d' },
  { value: '28d', label: '28d' },
  { value: '3m', label: '3m' },
  { value: '6m', label: '6m' },
  { value: '12m', label: '12m' },
  { value: '16m', label: '16m' },
];

const COLORS = ['#2D3250', '#b1c936', '#3A4070', '#6B7280', '#10B981'];

type EmailSortField = 'name' | 'subject' | 'sentDate' | 'sent' | 'openRate' | 'clickRate';
type QuerySortField = 'query' | 'clicks' | 'impressions' | 'ctr' | 'position';
type PageSortField = 'page' | 'clicks' | 'impressions' | 'ctr' | 'position';
type SortDir = 'asc' | 'desc';

function SectionLoading() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin h-6 w-6 border-3 border-gray-200 border-t-eep-navy rounded-full" />
    </div>
  );
}

function formatPct(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function formatCTR(value: number): string {
  return `${(value * 100).toFixed(2)}%`;
}

function formatPosition(value: number): string {
  return value.toFixed(1);
}

function truncateUrl(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname + u.search;
    return path.length > 60 ? path.slice(0, 57) + '...' : path;
  } catch {
    return url.length > 60 ? url.slice(0, 57) + '...' : url;
  }
}

export default function HubSpotMarketing() {
  const { forms, email, loading: hsLoading, error: hsError, refetch: hsRefetch } = useHubSpotMarketing();

  const [dateRange, setDateRange] = useState<GSCDateRange>('28d');
  const { summary, queries, pages, dailyTrend, devices, countries, loading: gscLoading, error: gscError, refetch: gscRefetch } =
    useSearchConsole(dateRange);

  // Email table sort state
  const [emailSortField, setEmailSortField] = useState<EmailSortField>('sentDate');
  const [emailSortDir, setEmailSortDir] = useState<SortDir>('desc');

  // Query table state
  const [querySortField, setQuerySortField] = useState<QuerySortField>('clicks');
  const [querySortDir, setQuerySortDir] = useState<SortDir>('desc');
  const [querySearch, setQuerySearch] = useState('');

  // Page table state
  const [pageSortField, setPageSortField] = useState<PageSortField>('clicks');
  const [pageSortDir, setPageSortDir] = useState<SortDir>('desc');
  const [pageSearch, setPageSearch] = useState('');

  const sortedEmails = useMemo(() => {
    if (!email) return [];
    return [...email.campaigns].sort((a, b) => {
      const av = a[emailSortField];
      const bv = b[emailSortField];
      if (typeof av === 'string' && typeof bv === 'string') {
        return emailSortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return emailSortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
  }, [email, emailSortField, emailSortDir]);

  const sortedQueries = useMemo(() => {
    if (!queries) return [];
    let filtered = queries;
    if (querySearch) {
      const q = querySearch.toLowerCase();
      filtered = queries.filter((r) => r.query.toLowerCase().includes(q));
    }
    return [...filtered].sort((a, b) => {
      const av = a[querySortField];
      const bv = b[querySortField];
      if (typeof av === 'string' && typeof bv === 'string') {
        return querySortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return querySortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
  }, [queries, querySortField, querySortDir, querySearch]);

  const sortedPages = useMemo(() => {
    if (!pages) return [];
    let filtered = pages;
    if (pageSearch) {
      const q = pageSearch.toLowerCase();
      filtered = pages.filter((r) => r.page.toLowerCase().includes(q));
    }
    return [...filtered].sort((a, b) => {
      const av = a[pageSortField];
      const bv = b[pageSortField];
      if (typeof av === 'string' && typeof bv === 'string') {
        return pageSortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return pageSortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
  }, [pages, pageSortField, pageSortDir, pageSearch]);

  const toggleEmailSort = (field: EmailSortField) => {
    if (emailSortField === field) {
      setEmailSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setEmailSortField(field);
      setEmailSortDir('desc');
    }
  };

  const toggleQuerySort = (field: QuerySortField) => {
    if (querySortField === field) {
      setQuerySortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setQuerySortField(field);
      setQuerySortDir('desc');
    }
  };

  const togglePageSort = (field: PageSortField) => {
    if (pageSortField === field) {
      setPageSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setPageSortField(field);
      setPageSortDir('desc');
    }
  };

  const EmailSortIcon = ({ field }: { field: EmailSortField }) => {
    if (emailSortField !== field) return null;
    return emailSortDir === 'asc' ? (
      <ChevronUpIcon className="w-3 h-3 inline ml-0.5" />
    ) : (
      <ChevronDownIcon className="w-3 h-3 inline ml-0.5" />
    );
  };

  const QuerySortIcon = ({ field }: { field: QuerySortField }) => {
    if (querySortField !== field) return null;
    return querySortDir === 'asc' ? (
      <ChevronUpIcon className="w-3 h-3 inline ml-0.5" />
    ) : (
      <ChevronDownIcon className="w-3 h-3 inline ml-0.5" />
    );
  };

  const PageSortIcon = ({ field }: { field: PageSortField }) => {
    if (pageSortField !== field) return null;
    return pageSortDir === 'asc' ? (
      <ChevronUpIcon className="w-3 h-3 inline ml-0.5" />
    ) : (
      <ChevronDownIcon className="w-3 h-3 inline ml-0.5" />
    );
  };

  const top10Countries = useMemo(() => {
    if (!countries) return [];
    return [...countries].sort((a, b) => b.clicks - a.clicks).slice(0, 10);
  }, [countries]);

  const loading = hsLoading || gscLoading;
  const error = hsError || gscError;
  const noData = !forms && !email && !summary && !dailyTrend;

  if (loading && noData) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-eep-navy rounded-full" />
      </div>
    );
  }

  if (error && noData) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => { hsRefetch(); gscRefetch(); }}
          className="px-4 py-2 bg-eep-navy text-white rounded-lg hover:bg-eep-navy-light transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketing</h1>
          <p className="text-sm text-gray-500 mt-1">HubSpot and Google Search Console performance</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            {DATE_RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setDateRange(r.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  dateRange === r.value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => { hsRefetch(); gscRefetch(); }}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* ═══ RECENT SUBMISSIONS ═══ */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Submissions</h2>
          {!forms ? (
            <SectionLoading />
          ) : forms.recentSubmissions.length > 0 ? (
            <div className="overflow-x-auto max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-gray-200 text-left text-gray-600">
                    <th className="pb-2 pr-4 font-medium">Form</th>
                    <th className="pb-2 pr-4 font-medium">Email</th>
                    <th className="pb-2 font-medium text-right">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {forms.recentSubmissions.map((sub, idx) => (
                    <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 pr-4 font-medium text-gray-900 whitespace-nowrap">{sub.formName}</td>
                      <td className="py-2 pr-4 text-gray-500">{sub.email}</td>
                      <td className="py-2 text-gray-400 text-right whitespace-nowrap">{formatDate(sub.submittedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No recent submissions</p>
          )}
        </div>

        {/* ═══ EMAIL MARKETING ═══ */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Marketing</h2>
          {!email ? (
            <SectionLoading />
          ) : (
            <div>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-gray-200 text-left text-gray-600">
                      {([
                        ['name', 'Campaign'],
                        ['subject', 'Subject'],
                        ['sentDate', 'Sent Date'],
                        ['sent', 'Sent'],
                        ['openRate', 'Open Rate'],
                        ['clickRate', 'Click Rate'],
                      ] as [EmailSortField, string][]).map(([field, label]) => (
                        <th
                          key={field}
                          className="pb-2 pr-4 font-medium cursor-pointer hover:text-gray-900 select-none"
                          onClick={() => toggleEmailSort(field)}
                        >
                          {label}
                          <EmailSortIcon field={field} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedEmails.map((row: EmailRow) => (
                      <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 pr-4 font-medium text-gray-900 max-w-xs truncate">{row.name}</td>
                        <td className="py-2 pr-4 text-gray-600 max-w-xs truncate">{row.subject}</td>
                        <td className="py-2 pr-4 text-gray-500">{formatDate(row.sentDate)}</td>
                        <td className="py-2 pr-4">{formatNumber(row.sent)}</td>
                        <td className="py-2 pr-4">{formatPct(row.openRate)}</td>
                        <td className="py-2 pr-4">{formatPct(row.clickRate)}</td>
                      </tr>
                    ))}
                    {sortedEmails.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-gray-500">No email campaigns found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* ═══ SEARCH CONSOLE KPIs ═══ */}
        {summary && (
          <div className="grid grid-cols-4 gap-4">
            <KPICard title="Total Clicks" value={formatNumber(summary.totalClicks)} />
            <KPICard title="Total Impressions" value={formatNumber(summary.totalImpressions)} />
            <KPICard title="Avg CTR" value={formatCTR(summary.avgCTR)} />
            <KPICard title="Avg Position" value={formatPosition(summary.avgPosition)} />
          </div>
        )}

        {/* ═══ PERFORMANCE OVER TIME ═══ */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Performance Over Time</h2>
          {!dailyTrend ? (
            <SectionLoading />
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={dailyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(d: string) => {
                    const date = new Date(d);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                <Tooltip
                  labelFormatter={(d: string) => new Date(d).toLocaleDateString()}
                  formatter={(value: number, name: string) => [formatNumber(value), name]}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="clicks" fill="#2D3250" radius={[2, 2, 0, 0]} name="Clicks" />
                <Line yAxisId="right" type="monotone" dataKey="impressions" stroke="#b1c936" strokeWidth={2} dot={false} name="Impressions" />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* ═══ DEVICE + COUNTRIES ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Device Breakdown</h2>
            {!devices ? (
              <SectionLoading />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={devices.map((d) => ({ name: d.device, value: d.clicks }))}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={true}
                  >
                    {devices.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatNumber(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Countries</h2>
            {!countries ? (
              <SectionLoading />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={top10Countries} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="country" tick={{ fontSize: 11 }} width={40} />
                  <Tooltip formatter={(value: number) => formatNumber(value)} />
                  <Bar dataKey="clicks" fill="#2D3250" radius={[0, 4, 4, 0]} name="Clicks" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* ═══ TOP QUERIES ═══ */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Top Queries {queries && `(${queries.length})`}
            </h2>
            <input
              type="text"
              placeholder="Search queries..."
              value={querySearch}
              onChange={(e) => setQuerySearch(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-eep-navy/20 focus:border-eep-navy w-64"
            />
          </div>
          {!queries ? (
            <SectionLoading />
          ) : (
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-gray-200 text-left text-gray-600">
                    {([
                      ['query', 'Query'],
                      ['clicks', 'Clicks'],
                      ['impressions', 'Impressions'],
                      ['ctr', 'CTR'],
                      ['position', 'Position'],
                    ] as [QuerySortField, string][]).map(([field, label]) => (
                      <th
                        key={field}
                        className="pb-2 pr-4 font-medium cursor-pointer hover:text-gray-900 select-none"
                        onClick={() => toggleQuerySort(field)}
                      >
                        {label}
                        <QuerySortIcon field={field} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedQueries.map((row: GSCQueryRow) => (
                    <tr key={row.query} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 pr-4 font-medium text-gray-900 max-w-md truncate">{row.query}</td>
                      <td className="py-2 pr-4">{formatNumber(row.clicks)}</td>
                      <td className="py-2 pr-4">{formatNumber(row.impressions)}</td>
                      <td className="py-2 pr-4">{formatCTR(row.ctr)}</td>
                      <td className="py-2 pr-4">{formatPosition(row.position)}</td>
                    </tr>
                  ))}
                  {sortedQueries.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-gray-500">
                        {querySearch ? 'No queries match your search' : 'No query data available'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ═══ TOP PAGES ═══ */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Top Pages {pages && `(${pages.length})`}
            </h2>
            <input
              type="text"
              placeholder="Search pages..."
              value={pageSearch}
              onChange={(e) => setPageSearch(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-eep-navy/20 focus:border-eep-navy w-64"
            />
          </div>
          {!pages ? (
            <SectionLoading />
          ) : (
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-gray-200 text-left text-gray-600">
                    {([
                      ['page', 'Page'],
                      ['clicks', 'Clicks'],
                      ['impressions', 'Impressions'],
                      ['ctr', 'CTR'],
                      ['position', 'Position'],
                    ] as [PageSortField, string][]).map(([field, label]) => (
                      <th
                        key={field}
                        className="pb-2 pr-4 font-medium cursor-pointer hover:text-gray-900 select-none"
                        onClick={() => togglePageSort(field)}
                      >
                        {label}
                        <PageSortIcon field={field} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedPages.map((row: GSCPageRow) => (
                    <tr key={row.page} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 pr-4 max-w-md">
                        {isSafeUrl(row.page) ? (
                          <a
                            href={row.page}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline truncate block"
                            title={row.page}
                          >
                            {truncateUrl(row.page)}
                          </a>
                        ) : (
                          <span className="text-gray-500 truncate block" title={row.page}>
                            {truncateUrl(row.page)}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-4">{formatNumber(row.clicks)}</td>
                      <td className="py-2 pr-4">{formatNumber(row.impressions)}</td>
                      <td className="py-2 pr-4">{formatCTR(row.ctr)}</td>
                      <td className="py-2 pr-4">{formatPosition(row.position)}</td>
                    </tr>
                  ))}
                  {sortedPages.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-gray-500">
                        {pageSearch ? 'No pages match your search' : 'No page data available'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
